import config from "../Config";
import {auth} from "../FirebaseSetup/auth";
import {IQueryParams} from "./Models/IQueryParams";

export type RequestOptions = {
    url: string;
    method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
    data?: any,
    queryParams?: IQueryParams | null,
    responseType?: 'blob' | 'json' | 'text' | undefined,
}

export type MutateData<T> = {
    id?: number | undefined,
    data?: T
}

export type AppMutateOptions = {
    onMutate?: () => void;
    onSuccess?: (data: any) => void;
    onError?: (data: unknown) => void;
}

export const apiCall = async <T>(options: RequestOptions): Promise<T> => {

    const token = await auth.currentUser?.getIdToken() ?? '';

    let headers: Record<string, string> = {
        'XAppPlatform': 'Web',
        'Authorization': `Bearer ${token}`
    };

    if (options.data) {
        headers['Content-Type'] = 'application/json';
    }

    const baseURL = config.baseUrl;
    let url = `${baseURL}${options.url}`;

    if (options['queryParams']) {
        url = addQueryParams(url, options['queryParams'])
    }

    try {
        const response = await fetch(url, {
            method: options.method || 'GET',
            headers,
            body: options.data ? JSON.stringify(options.data) : undefined,
        });

        if (response.status >= 501) {
            const error: any = new Error(response.statusText);

            switch (response.status) {
                case 500:
                    error.code = "INTERNAL_SERVER_ERROR";
                    break;
                default:
                    error.code = "SERVICE_UNAVAILABLE";
            }

            return Promise.reject(error);
        }

        let data = null;

        try {
            switch (options.responseType ?? 'json') {
                case "blob":
                    data = await response.blob();
                    break;
                case "text":
                    data = await response.text();
                    break;
                case "json":
                default:
                    data = await response.json();
            }

        }
        catch (e) {
            console.log('Error parsing JSON:', e);
        }

        if (!response.ok) {

            const error: any = new Error(data?.message || response.statusText);
            error.response = {
                status: response.status,
                data: data,
                headers: response.headers
            };
            error.config = {
                method: options.method,
                url: options.url
            };

            console.log('Request Failed:', error);
            console.log('Status:', error.response.status);
            console.log('Data:', error.response.data);
            console.log('Headers:', error.response.headers);

            const ignoreStatusCodes = [400, 402, 404, 413] //BadRequest, Payment required, Not found, Content too large
            const ignoredMessages = ["Request aborted", "Network Error", "timeout exceeded"];

            if (!ignoreStatusCodes.includes(error?.response?.status) && !ignoredMessages.includes(error.message)) {
                log(error.message, error.stack, {
                    method: error.config?.method,
                    url: error.config?.url,
                    responseData: data
                }).then();
            }

            return Promise.reject(error);
        }

        return data;
    } catch (error: any) {
        console.log('Request Failed:', error);
        console.log('Error Message:', error.message);

        const ignoredMessages = ["Request aborted", "Network Error", "timeout exceeded"];

        if (!ignoredMessages.includes(error.message)) {
            log(error.message, error.stack, {
                method: options.method,
                url: options.url
            }).then();
        }

        error.code = "ERR_NETWORK"

        return Promise.reject(error);
    }
}

export const log = async (errorMessage: string, errorStack: string, properties: any = null, level: string = 'Error') => {
    const token = await auth.currentUser?.getIdToken() ?? '';

    let headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
    };

    const baseURL = config.baseUrl + '/logging';

    const logEvent = {
        serviceName: 'AnotherSystem.Frontend',
        messageTemplate: errorMessage || 'No error message',
        timestamp: new Date(),
        properties: properties,
        level: level,
        error: {
            message: errorMessage || 'No error message',
            stacktrace: errorStack || 'No error stack',
        },
    };

    try {
        await fetch(baseURL, {
            method: 'POST',
            headers,
            body: JSON.stringify(logEvent)
        });
    } catch (e) {
        console.error('Failed to log error', e);
    }
}

const addQueryParams = (url: string, queryParams: Partial<IQueryParams> | null) => {
    if (!queryParams) {
        return url;
    }

    const params = new URLSearchParams();

    Object.keys(queryParams).forEach(key => {
        if (queryParams[key] !== undefined && queryParams[key] !== null) {

            if (Array.isArray((queryParams[key]))) {
                let valueArray = queryParams[key] as any[]
                valueArray.forEach((value: any) => {
                    params.append(key, value!.toString());
                })
            } else {
                params.append(key, queryParams[key]!.toString());
            }
        }
    });

    return `${url}?${params.toString()}`
}